/** One server-confirmed progress sink for native and external lesson players. */
export function createLearningMediaProgress({ save, readPosition = result => result?.position_seconds, onAccepted = () => {}, onError = () => {}, isCurrent = () => true, now = Date.now, schedule = setTimeout, cancel = clearTimeout, interval = 15000 }) {
  let stopped = false;
  let saving = false;
  let pending = null;
  let timer = null;
  let lastAttempt = -Infinity;
  let confirmed = -1;
  let attempted = -1;
  let retryAfterFailure = false;
  const active = () => !stopped && isCurrent();
  const flush = async () => {
    if (!active() || saving || !pending) return;
    const wait = interval - (now() - lastAttempt);
    if ((!pending.flush || retryAfterFailure) && wait > 0) {
      if (timer === null) timer = schedule(() => { timer = null; flush(); }, wait);
      return;
    }
    cancel(timer); timer = null;
    const sample = pending; pending = null;
    saving = true; attempted = sample.seconds; lastAttempt = now();
    let failed = false;
    try {
      const result = await save(sample.seconds);
      const position = readPosition(result);
      if (Number.isFinite(position) && position >= 0) confirmed = Math.max(confirmed, Math.min(604800, Math.floor(position)));
      retryAfterFailure = false;
      if (active()) onAccepted(result);
    } catch (error) {
      failed = true;
      retryAfterFailure = true;
      if (active()) onError(error);
      // No optimistic completion or automatic retry loop after access/network failure.
      // A subsequent real player sample can retry the still-unconfirmed position.
      if (!pending) pending = sample;
    } finally {
      saving = false; attempted = -1;
      if (pending && pending.seconds <= confirmed) pending = null;
      if (active() && pending && !failed) flush();
    }
  };
  return {
    onTime(position) {
      if (!active() || !Number.isFinite(position?.seconds) || position.seconds < 0) return;
      const seconds = Math.min(604800, Math.floor(position.seconds));
      if (seconds <= confirmed || (saving && seconds <= attempted)) return;
      pending = { seconds: Math.max(seconds, pending?.seconds || 0), flush: Boolean(position.ended || position.paused || pending?.flush) };
      flush();
    },
    dispose() { stopped = true; pending = null; cancel(timer); timer = null; },
  };
}
